import { apiClient } from './api-client';
import { getAccessToken } from '@/lib/tokenStorage';

// Same base the API client uses; needed for the unload-time request below.
const BFF_URL = import.meta.env.VITE_BFF_URL || '/api';

// ==================== Types ====================

/** Whether a tenant may call customers, and from what number to where. */
export interface OutboundSettings {
  enabled: boolean;
  callerId?: string;
  /** ISO country codes that may be called. */
  allowedCountries?: string[];
}

/** A call placed to a customer, as the server first reports it. */
export interface OutboundCallStarted {
  callId: string;
  state: string;
  transport?: string;
}

/** A call placed to a customer, as the server reports it later. */
export interface OutboundCallView {
  callId: string;
  ticketId?: string;
  customerNumber: string;
  state: string;
  answeredAt?: string;
}

/** Which carrier answers this tenant's calls. */
export type TelephonyProvider = 'twilio' | 'exotel';

/**
 * Setup details for the Exotel tab. The applet URL is built server-side
 * because only the gateway knows its own public webhook host.
 */
export interface ExotelSetup {
  appletUrl: string;
  streamConfigured: boolean;
}

export interface VoiceConfig {
  id: string;
  tenantId: string;
  /** Null on every tenant created before Exotel existed, meaning Twilio. */
  provider?: TelephonyProvider | null;
  /** The ExoPhone, when the provider is Exotel. */
  phoneNumber?: string | null;
  /** Exotel API details, used only to fetch call recordings. The token itself is never returned. */
  exotelAccountSid?: string | null;
  exotelApiKey?: string | null;
  exotelSubdomain?: string | null;
  exotelApiTokenSet?: boolean;
  /** Calling customers. Null until an admin sets it. */
  outbound?: OutboundSettings | null;
  twilioPhoneNumber: string;
  flexWorkspaceSid?: string;
  ttsProvider: 'twilio' | 'elevenlabs';
  ttsBackupProvider?: 'twilio' | 'elevenlabs';
  greeting?: string;
  language: string;
  handoffThreshold: number;
  recordCalls: boolean;
  transcribeCalls: boolean;
  /** IVR behaviour. Currently carries the spoken language menu. */
  ivrConfig?: IvrConfig | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * IVR behaviour, stored as JSON on the voice config.
 *
 * `languageSelection` drives the spoken menu the caller hears after the
 * greeting. Fewer than two options means there is nothing to choose, so the
 * server treats it as disabled however this flag is set.
 */
export interface IvrConfig {
  languageSelection?: {
    enabled: boolean;
    /** BCP-47 codes, in the order they are spoken to the caller. */
    options: string[];
  };
}

/** Where a human handoff is in its lifecycle (see the channel-gateway orchestrator). */
export type HandoffState =
  | 'requested'
  | 'queued'
  | 'agent_reserved'
  | 'connecting'
  | 'connected'
  | 'ended'
  | 'fallback'
  | 'abandoned'
  | 'failed';

export interface VoiceHandoff {
  state: HandoffState;
  attemptId: string;
  requestedAt: string;
  reservedAgentId?: string;
  queuedAt?: string;
  connectedAt?: string;
  reason?: string;
  /** Colleagues added to the call (a 3-way call). */
  participants?: Array<{ agentId: string; agentName?: string; attemptId: string; state: 'ringing' | 'joined' }>;
}

export interface VoiceTranscriptTurn {
  speaker: 'customer' | 'ai' | 'agent' | 'system';
  content: string;
  timestamp: string;
  /** An English rendering, when the turn was spoken in another language. */
  contentEnglish?: string;
  /** The language the turn was spoken in (BCP-47, e.g. "mr-IN"), when known. */
  language?: string;
  /** The agent's name, on a turn an agent spoke. */
  speakerName?: string;
}

/** One agent on the roster. */
export interface RosterAgent {
  agentId: string;
  agentName?: string;
  /** reserved: their phone is ringing. busy: on a call. */
  status: 'available' | 'reserved' | 'busy' | 'offline';
  /** The call they are ringing for or on, and its caller. */
  sessionId?: string;
  callerNumber?: string;
  /** Epoch ms their console last checked in, or went offline. */
  lastSeenAt?: number;
  /** Online, but their console stopped checking in: not offered calls. */
  stale?: boolean;
}

/** What the server says about how this agent's browser rings (see getAgentToken). */
export interface AgentPhoneSetup {
  token: string | null;
  identity: string;
  transport?: 'device' | 'media_bridge';
  /** media_bridge: the gateway origin the agent's media socket connects to. */
  mediaUrl?: string;
}

export interface VoiceSession {
  sessionId: string;
  callSid: string;
  from: string;
  to: string;
  status: 'ringing' | 'ai_conversation' | 'handoff_pending' | 'with_agent' | 'completed' | 'failed';
  messageCount: number;
  lastTranscript?: string;
  callerCity?: string;
  callerCountry?: string;
  createdAt: string;
  /** Last change; for an ended call, roughly when it ended. */
  updatedAt?: string;
  handoffReason?: string;
  customerId?: string;
  ticketId?: string;
  /** Present once a human handoff has been requested (handoff engine on). */
  handoff?: VoiceHandoff;
  transcriptHistory?: VoiceTranscriptTurn[];
  /** A written summary of the AI conversation, when one was produced. */
  aiSummaryText?: string;
  /** The context every agent offered this call receives; `aiSummary` is JSON. */
  agentCallParams?: Record<string, string>;
  /** What the AI has already collected from the caller, by field key. */
  collectedDetails?: Record<string, unknown>;
  /** The kind of request the AI filed this as. */
  ticketTypeName?: string;
  /** The agent who took the call, by name. */
  handledByName?: string;
  /**
   * Set when this agent may see that the caller is waiting but not what they
   * told the AI: the conversation shows once the call is theirs.
   */
  conversationRestricted?: boolean;
  /** The call's ticket, with the agent it is assigned to. */
  ticket?: CallTicket;
}

export interface VoiceCallLog {
  id: string;
  callSid: string;
  sessionId: string;
  from: string;
  to: string;
  direction: 'inbound' | 'outbound';
  status: string;
  endReason?: string;
  aiHandled: boolean;
  aiMessageCount: number;
  recordingUrl?: string;
  /**
   * Whether there is a recording to play from our own store: held
   * ('available'), worth asking the provider for ('check'), or none.
   */
  recording?: 'available' | 'check' | 'none';
  startedAt: string;
  endedAt?: string;
  durationSeconds?: number;
  callerCity?: string;
  callerCountry?: string;
  /** The agent who took the call over from the AI, if one did. */
  handledByAgentId?: string | null;
  handledByName?: string | null;
  /** The call's ticket, with the agent it is assigned to. */
  ticket?: CallTicket | null;
}

/** A call's ticket as history shows it. */
export interface CallTicket {
  id: string;
  ticketNumber: string;
  status?: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
}

export interface VoiceStats {
  active: number;
  aiHandled: number;
  pendingHandoff: number;
  withAgent: number;
}

export interface LiveCallStats {
  liveCalls: number;
  completedToday: number;
  completedByAI: number;
  completedByHuman: number;
  pendingHandoff: number;
}

export interface VoiceCallStats {
  totalCalls: number;
  completedCalls: number;
  aiResolvedCalls: number;
  handoffCalls: number;
  aiResolutionRate: number;
  averageDuration: number;
  averageAiMessages: number;
}

export interface CreateVoiceConfigDto {
  outbound?: OutboundSettings;
  provider?: TelephonyProvider;
  /** ExoPhone number; Exotel only. */
  phoneNumber?: string;
  /** Exotel API details for fetching recordings. A blank token keeps the saved one. */
  exotelAccountSid?: string;
  exotelApiKey?: string;
  exotelApiToken?: string;
  exotelSubdomain?: string;
  // Required by the API unless `provider` is 'exotel'.
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioPhoneNumber: string;
  flexWorkspaceSid?: string;
  flexWorkflowSid?: string;
  flexQueueSid?: string;
  ttsProvider?: 'twilio' | 'elevenlabs';
  ttsBackupProvider?: 'twilio' | 'elevenlabs';
  elevenLabsApiKey?: string;
  elevenLabsVoiceId?: string;
  twilioVoice?: string;
  greeting?: string;
  language?: string;
  handoffThreshold?: number;
  recordCalls?: boolean;
  transcribeCalls?: boolean;
  businessHours?: Record<string, any>;
  ivrConfig?: IvrConfig;
  afterHoursMessage?: string;
  enabled?: boolean;
}

export interface UpdateVoiceConfigDto extends Partial<CreateVoiceConfigDto> {}

export interface FlexTransferDto {
  targetAgentId?: string;
  targetQueueSid?: string;
  reason?: string;
  context?: string;
}

// Helper to build request config with tenant header override
const withTenantHeader = (tenantId?: string) => {
  if (!tenantId) return {};
  return {
    headers: {
      'X-Tenant-Id': tenantId,
    },
  };
};

// ==================== Voice Service ====================

export const voiceService = {
  // Configuration
  async getConfig(tenantId?: string): Promise<VoiceConfig | null> {
    const response = await apiClient.get('/channel-gateway/voice/configs', withTenantHeader(tenantId));
    return response.data;
  },

  /** Applet URL and server readiness for the Exotel settings tab. */
  async getExotelSetup(tenantId?: string): Promise<ExotelSetup> {
    const response = await apiClient.get(
      '/channel-gateway/voice/exotel/setup',
      withTenantHeader(tenantId),
    );
    return response.data;
  },

  async createConfig(dto: CreateVoiceConfigDto, tenantId?: string): Promise<VoiceConfig> {
    const response = await apiClient.post('/channel-gateway/voice/configs', dto, withTenantHeader(tenantId));
    return response.data;
  },

  async updateConfig(dto: UpdateVoiceConfigDto, tenantId?: string): Promise<VoiceConfig> {
    const response = await apiClient.put('/channel-gateway/voice/configs', dto, withTenantHeader(tenantId));
    return response.data;
  },

  async deleteConfig(tenantId?: string): Promise<void> {
    await apiClient.delete('/channel-gateway/voice/configs', withTenantHeader(tenantId));
  },

  async testConnection(tenantId?: string): Promise<{ success: boolean; error?: string }> {
    const response = await apiClient.post('/channel-gateway/voice/configs/test', {}, withTenantHeader(tenantId));
    return response.data;
  },

  // Active Sessions
  async getActiveSessions(): Promise<VoiceSession[]> {
    const response = await apiClient.get('/channel-gateway/voice/sessions');
    return response.data;
  },

  async getSessionStats(): Promise<VoiceStats> {
    const response = await apiClient.get('/channel-gateway/voice/sessions/stats');
    return response.data;
  },

  async getLiveCallStats(): Promise<LiveCallStats> {
    const response = await apiClient.get('/channel-gateway/voice/sessions/stats/live');
    return response.data;
  },

  async cleanupStaleSessions(): Promise<{ cleaned: number }> {
    const response = await apiClient.post('/channel-gateway/voice/sessions/cleanup');
    return response.data;
  },

  async forceCleanAllSessions(): Promise<{ cleaned: number }> {
    const response = await apiClient.post('/channel-gateway/voice/sessions/force-cleanup');
    return response.data;
  },

  async getSession(sessionId: string): Promise<VoiceSession> {
    const response = await apiClient.get(`/channel-gateway/voice/sessions/${sessionId}`);
    return response.data;
  },

  // Handoff / Transfer
  async initiateTransfer(sessionId: string, dto: FlexTransferDto): Promise<{ success: boolean; taskSid?: string }> {
    const response = await apiClient.post(`/channel-gateway/voice/sessions/${sessionId}/transfer`, dto);
    return response.data;
  },

  // Call Logs
  async getCallLogs(params?: {
    from?: string;
    to?: string;
    status?: string;
    limit?: number;
    offset?: number;
    /** Admins: one agent's calls. Ignored for agents, who always get their own. */
    agentId?: string;
  }): Promise<{ data: VoiceCallLog[]; total: number }> {
    const response = await apiClient.get('/channel-gateway/voice/logs', { params });
    // Tolerate the previous bare-array response while the channel-gateway
    // is being deployed — once everywhere is on the new shape the array
    // branch can be deleted.
    if (Array.isArray(response.data)) {
      return { data: response.data, total: response.data.length };
    }
    return response.data;
  },

  async getCallStats(from: string, to: string, agentId?: string): Promise<VoiceCallStats> {
    const response = await apiClient.get('/channel-gateway/voice/logs/stats', {
      params: { from, to, ...(agentId && { agentId }) },
    });
    return response.data;
  },

  async getCallLog(callSid: string): Promise<VoiceCallLog> {
    const response = await apiClient.get(`/channel-gateway/voice/logs/${callSid}`);
    return response.data;
  },

  /**
   * The recording of the call a ticket came from, for anyone who can open the
   * ticket. Rejects with a 404 when there is none.
   */
  async getTicketCallRecording(ticketId: string): Promise<Blob> {
    const response = await apiClient.get(
      `/channel-gateway/voice/tickets/${encodeURIComponent(ticketId)}/recording`,
      { responseType: 'blob', timeout: 120000 },
    );
    return response.data;
  },

  /**
   * The call's recording audio. Rejects with a 404 when the call has none.
   * The first play of a call can take a few seconds while it is fetched.
   */
  async getCallRecording(callSid: string): Promise<Blob> {
    const response = await apiClient.get(
      `/channel-gateway/voice/logs/${encodeURIComponent(callSid)}/recording`,
      { responseType: 'blob', timeout: 120000 },
    );
    return response.data;
  },

  // Utilities
  formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  },

  formatPhoneNumber(phone: string): string {
    // Simple formatting for US numbers
    if (phone.startsWith('+1') && phone.length === 12) {
      return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
    }
    return phone;
  },

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      ringing: 'yellow',
      ai_conversation: 'blue',
      handoff_pending: 'orange',
      with_agent: 'green',
      completed: 'gray',
      failed: 'red',
    };
    return colors[status] || 'gray';
  },

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      ringing: 'Ringing',
      ai_conversation: 'With AI',
      handoff_pending: 'Handoff Pending',
      with_agent: 'With Agent',
      completed: 'Completed',
      failed: 'Failed',
    };
    return labels[status] || status;
  },

  // ==================== Native Agent Handoff ====================

  /**
   * How this agent's browser rings. `device`: a Twilio softphone, with its
   * token. `media_bridge`: no token — calls arrive over the platform's own
   * media bridge (BridgePhone), so the browser is ready as it is.
   */
  async getAgentToken(agentId: string, agentName?: string): Promise<AgentPhoneSetup> {
    const params = new URLSearchParams({ agentId });
    if (agentName) params.append('agentName', agentName);
    const response = await apiClient.get<AgentPhoneSetup>(
      `/channel-gateway/voice/agent/token?${params.toString()}`,
    );
    return response.data;
  },

  async setAgentOnline(agentId: string, agentName?: string): Promise<{ queued: number }> {
    const response = await apiClient.post<{ queued: number }>(
      '/channel-gateway/voice/agent/online',
      { agentId, agentName },
    );
    return response.data;
  },

  async setAgentOffline(agentId: string): Promise<void> {
    await apiClient.post('/channel-gateway/voice/agent/offline', { agentId });
  },

  /** Keeps the agent registered as online; online=false means re-register. */
  async agentHeartbeat(): Promise<{ online: boolean }> {
    const response = await apiClient.post<{ online: boolean }>('/channel-gateway/voice/agent/heartbeat', {});
    return response.data;
  },

  /** Take a waiting caller from the queue; the agent's phone rings. 409 when it can't. */
  /**
   * Take a caller: one waiting in the queue, or one ringing for a colleague.
   * Rejects (409) when another agent got there first. `ringingYou` when it
   * was already ringing for this agent.
   */
  async claimHandoff(sessionId: string): Promise<{ claimed: boolean; ringingYou?: boolean }> {
    const response = await apiClient.post<{ claimed: boolean; ringingYou?: boolean }>(
      `/channel-gateway/voice/handoff/${encodeURIComponent(sessionId)}/claim`,
      {},
    );
    return response.data;
  },

  /** Decline a call ringing for this agent, so the caller moves on at once. */
  /**
   * Pass this agent's call - ringing, or connected - to a colleague, or to
   * the next available agent when none is named. Rejects (409) when it could
   * not be transferred; the call then stays with this agent.
   */
  async transferCall(
    sessionId: string,
    transfer: { targetAgentId?: string; note?: string },
  ): Promise<{ result: string }> {
    const response = await apiClient.post<{ result: string }>(
      `/channel-gateway/voice/handoff/${encodeURIComponent(sessionId)}/transfer`,
      transfer,
    );
    return response.data;
  },

  /** Add a colleague to this agent's call (a 3-way call); omit the colleague for whoever is free next. */
  async inviteToCall(sessionId: string, invite: { targetAgentId?: string }): Promise<{ result: string }> {
    const response = await apiClient.post<{ result: string }>(
      `/channel-gateway/voice/handoff/${encodeURIComponent(sessionId)}/conference`,
      invite,
    );
    return response.data;
  },

  /** Cancel a colleague's invitation to this agent's call, or take them off it. */
  async removeFromCall(sessionId: string, agentId: string): Promise<{ removed: boolean }> {
    const response = await apiClient.delete<{ removed: boolean }>(
      `/channel-gateway/voice/handoff/${encodeURIComponent(sessionId)}/conference/${encodeURIComponent(agentId)}`,
    );
    return response.data;
  },

  async getOutboundAvailability(): Promise<{ available: boolean }> {
    const response = await apiClient.get<{ available: boolean }>('/channel-gateway/voice/outbound/calls/availability');
    return response.data;
  },

  /** The server takes the number from the ticket. The key makes a repeated request the same call. */
  async startOutboundCall(ticketId: string, idempotencyKey: string): Promise<OutboundCallStarted> {
    const response = await apiClient.post<OutboundCallStarted>(
      `/channel-gateway/voice/tickets/${encodeURIComponent(ticketId)}/outbound-call`,
      {},
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data;
  },

  /** A number typed on the keypad. The server checks it, including do-not-call. */
  async dialOutboundCall(number: string, idempotencyKey: string): Promise<OutboundCallStarted> {
    const response = await apiClient.post<OutboundCallStarted>(
      '/channel-gateway/voice/outbound/calls',
      { number },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return response.data;
  },

  async getActiveOutboundCall(): Promise<OutboundCallView | null> {
    const response = await apiClient.get<OutboundCallView | null>('/channel-gateway/voice/outbound/calls/active');
    return response.data || null;
  },

  async getOutboundCall(callId: string): Promise<OutboundCallView> {
    const response = await apiClient.get<OutboundCallView>(
      `/channel-gateway/voice/outbound/calls/${encodeURIComponent(callId)}`,
    );
    return response.data;
  },

  async endOutboundCall(callId: string): Promise<void> {
    await apiClient.post(`/channel-gateway/voice/outbound/calls/${encodeURIComponent(callId)}/end`, {});
  },

  async declineHandoff(sessionId: string): Promise<{ declined: boolean }> {
    const response = await apiClient.post<{ declined: boolean }>(
      `/channel-gateway/voice/handoff/${encodeURIComponent(sessionId)}/decline`,
      {},
    );
    return response.data;
  },

  /**
   * Best-effort "offline" while the tab is closing. A keepalive fetch survives
   * the page unloading; an axios request does not. The agent is the token's
   * owner, so no id is sent.
   */
  setAgentOfflineOnUnload(): void {
    const token = getAccessToken();
    if (!token) return;
    try {
      void fetch(`${BFF_URL}/channel-gateway/voice/agent/offline`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: '{}',
      });
    } catch {
      // Nothing to do: the agent entry expires on its own.
    }
  },

  async getNativeHandoffStats(): Promise<{ availableAgents: number; queuedCalls: number }> {
    const response = await apiClient.get<{ availableAgents: number; queuedCalls: number }>(
      '/channel-gateway/voice/agent/stats',
    );
    return response.data;
  },

  /** Calls that ended in the last 10 minutes, newest first. */
  async getRecentlyEnded(): Promise<VoiceSession[]> {
    const response = await apiClient.get<VoiceSession[]>('/channel-gateway/voice/sessions/recent');
    return response.data;
  },

  /** Every agent online now or in the last day, busiest first. */
  async getAgentRoster(): Promise<RosterAgent[]> {
    const response = await apiClient.get<RosterAgent[]>('/channel-gateway/voice/agent/roster');
    return response.data;
  },
};

export default voiceService;
